import useSchedulingPage from "../../../hooks/ReactQueryHooks/useSchedulingPage";
import SchedulingPageControls from "./SchedulingPageControls";
import { Alert } from "@mui/material";
import React, { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import Table from "react-bootstrap/Table";
import { ArrowBack } from "@mui/icons-material";
import { Tooltip, Tabs, Tab, IconButton } from "@mui/material";
import { ScheduleEditorTheme, Button } from "./ScheduleEditorUI";
import { useConfirmDialog } from "../../../hooks/useConfirmDialog";
import { failed, success } from "../../common/Toastify";
import { useSchedulingOwner } from "../../../hooks/ReactQueryHooks/useSchedulingOwner";
import { useScheduleQuery, useSchedulesQuery } from "../../../hooks/ReactQueryHooks/useSchedulesQuery";
import { useAvailabilityRulesQuery } from "../../../hooks/ReactQueryHooks/useAvailabilityRulesQuery";
import { useServiceCategoriesQuery } from "../../../hooks/ReactQueryHooks/useServiceCategoriesQuery";
import { deactivateAvailabilityRule } from "../../api/Scheduling/availabilityRules";
import { listAvailabilityExceptions, deactivateAvailabilityException } from "../../api/Scheduling/availabilityExceptions";
import { listAvailability, deactivateAvailability } from "../../api/Scheduling/availability";
import { listBlocks, releaseBlock } from "../../api/Scheduling/blocks";
import { schedulingErrorMessage } from "../../api/Scheduling/errors";
import { practitionerIdFromUser } from "./names";
import { formatRange } from "./time";
import ScheduleForm from "./ScheduleForm";
import AvailabilityRuleForm from "./AvailabilityRuleForm";
import ExceptionForm from "./ExceptionForm";
import OccurrenceIssues from "./OccurrenceIssues";
import ManualAvailabilityForm from "./ManualAvailabilityForm";
import CalendarBlockForm from "./CalendarBlockForm";
import WorkScheduleSummary from "./WorkScheduleSummary";
import { timeZoneLabel } from "./timeZones";
import "./Scheduling.css";

const Panel = ({ title, description, action, children, hidden }) => (
  <div className="scheduling-panel" hidden={hidden}>
    <div className="scheduling-panel-header">
      <div className="scheduling-panel-titles">
        <h3>{title}</h3>
        {description ? <p className="scheduling-panel-desc">{description}</p> : null}
      </div>
      {action ? <div className="scheduling-panel-action">{action}</div> : null}
    </div>
    <div className="scheduling-panel-body">{children}</div>
  </div>
);

const EmptyState = ({ children }) => <p className="scheduling-empty">{children}</p>;

const ScheduleManage = () => {
  const location = useLocation();
  const [activeSection, setActiveSection] = useState("hours");
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { getConfirmation } = useConfirmDialog();
  const { excludedOrganizationRefs, organizationRef, practitionerRef } = useSchedulingOwner();
  const selectedPractitioner = location.state?.data;
  const targetPractitionerRef = selectedPractitioner
    ? practitionerIdFromUser(selectedPractitioner)
    : practitionerRef;

  const queryScheduleId = searchParams.get("scheduleId") || "";
  const stateScheduleId = location.state?.scheduleId || "";

  const { data: schedules = [], isLoading: schedulesLoading } = useSchedulesQuery({
    organizationRef,
    practitionerRef: targetPractitionerRef,
    status: "active",
    enabled: !!organizationRef && !!targetPractitionerRef && !queryScheduleId && !stateScheduleId,
  });

  const scheduleId = queryScheduleId || stateScheduleId || schedules[0]?.scheduleId || "";

  useEffect(() => {
    if (!scheduleId || queryScheduleId === scheduleId) return;
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.set("scheduleId", scheduleId);
        next.set("organizationRef", organizationRef);
        return next;
      },
      { replace: true, state: location.state }
    );
  }, [scheduleId, queryScheduleId, setSearchParams, location.state]);

  const { data: schedule } = useScheduleQuery({
    organizationRef,
    scheduleId,
    enabled: !!scheduleId,
  });
  const { data: rules = [], refetch: refetchRules, isLoading: rulesLoading, isError: rulesError } = useAvailabilityRulesQuery({
    organizationRef,
    scheduleId,
    active: true,
    enabled: !!scheduleId && (activeSection === "hours" || activeSection === "changes"),
  });
  const { data: categories = [] } = useServiceCategoriesQuery({ scope: "descendants", excludedOrganizationRefs, organizationRef, enabled: !!scheduleId });
  const activeRuleId = rules.find((rule) => rule.active)?.availabilityRuleId;
  const exceptionsPage = useSchedulingPage("scheduling-availability-exceptions", { organizationRef, availabilityRuleId: activeRuleId, active: true }, listAvailabilityExceptions, "availabilityExceptions", { enabled: !!activeRuleId && activeSection === "changes" });
  const windowsPage = useSchedulingPage("scheduling-availability", { organizationRef, scheduleId, active: true, manualOnly: true }, listAvailability, "availability", { enabled: !!scheduleId && activeSection === "changes" });
  const blocksPage = useSchedulingPage("scheduling-blocks", { organizationRef, scheduleId, status: "active" }, listBlocks, "blocks", { enabled: !!scheduleId && activeSection === "blocks" });
  const { data: exceptions } = exceptionsPage;
  const { data: windows } = windowsPage;
  const { data: blocks } = blocksPage;

  const manualWindows = useMemo(
    () =>
      windows.filter(
        (item) => !item.availabilityRuleId && !item.availabilityExceptionId
      ),
    [windows]
  );


  const [ruleModal, setRuleModal] = useState(null);
  const [exceptionModal, setExceptionModal] = useState(null);
  const [resolvingIssue, setResolvingIssue] = useState(false);
  const [windowModal, setWindowModal] = useState(null);
  const [blockModal, setBlockModal] = useState(null);

  const resolveOccurrence = async (rule, issue) => {
    setResolvingIssue(true);
    try {
      const result = await listAvailabilityExceptions({ organizationRef, availabilityRuleId: rule.availabilityRuleId,
        localDate: issue.localDate, active: true, consistency: "strong" });
      if (!Array.isArray(result?.availabilityExceptions)) throw new Error("Could not load date changes.");
      setExceptionModal({ ...result.availabilityExceptions[0], availabilityRuleId: rule.availabilityRuleId,
        localDate: issue.localDate, occurrenceIssue: issue });
    } catch (error) {
      failed(schedulingErrorMessage(error));
    } finally {
      setResolvingIssue(false);
    }
  };

  const confirmDeactivate = async (label, action) => {
    const confirmed = await getConfirmation({
      title: "Attention!",
      entryName: label,
      actionBtnTitle: "Deactivate",
    });
    if (!confirmed) return;
    try {
      await action();
      success("Deactivated");
    } catch (error) {
      failed(schedulingErrorMessage(error));
    }
  };

  const calendarState = { organizationRef, scheduleId, data: selectedPractitioner };

  if (!scheduleId) {
    if (schedulesLoading) {
      return (
        <section className="common-listing">
          <p className="scheduling-empty">Loading schedule…</p>
        </section>
      );
    }
    return <ScheduleForm />;
  }

  const cancelled = schedule?.status === "cancelled";

  return (
    <ScheduleEditorTheme>
      <section className="common-listing scheduling-manage scheduling-workspace schedule-edit-page">
        <div className="scheduling-manage-heading">
          <div className="scheduling-manage-heading-left">
            <Tooltip title="Go back">
              <IconButton aria-label="Go back" onClick={() => navigate(-1)}>
                <ArrowBack />
              </IconButton>
            </Tooltip>
            <div className="scheduling-manage-title">
              <h1>Work schedule</h1>
              {schedule?.timezoneName ? (
                <span className="scheduling-zone-label">Schedule time: {timeZoneLabel(schedule.timezoneName)}</span>
              ) : null}
            </div>
          </div>
          <Button
            variant="secondary"
            onClick={() => navigate("/app/schedule", { state: calendarState })}
          >
            Open calendar
          </Button>
        </div>

        <p className="schedule-page-description">Manage regular hours, make a change for a specific date, or set aside time.</p>
        {!cancelled && <OccurrenceIssues rules={rules} onResolve={resolveOccurrence} disabled={resolvingIssue} />}
        <Tabs className="schedule-editor-tabs" aria-label="Schedule sections" value={activeSection} onChange={(_, value) => setActiveSection(value)} variant="scrollable" scrollButtons={false}>
          {[["hours", "Regular hours"], ["changes", "Date changes"], ["blocks", "Blocked time"], ["settings", "Schedule settings"]].map(([value, label]) => (
            <Tab key={value} value={value} label={label} />
          ))}
        </Tabs>
        <div className="scheduling-manage-stack">
          <Panel
            hidden={activeSection !== "settings"}
            title="Schedule settings"
            description="Set the date range, timezone and appointment types for this schedule."
          >
            <ScheduleForm embedded scheduleId={scheduleId} />
          </Panel>

          {cancelled ? (
            <Panel title="Hours & blocks">
              <EmptyState>This schedule is cancelled. Create a new schedule to manage hours.</EmptyState>
            </Panel>
          ) : (
            <>
              <Panel
                hidden={activeSection !== "hours"}
                title="Regular working hours"
                description="Set your working days, hours and breaks. Repeat the same week or create a rotating schedule."
                action={<Button onClick={() => setRuleModal({})}>Add work schedule</Button>}
              >
                <WorkScheduleSummary rules={rules} categories={categories} isLoading={rulesLoading} isError={rulesError}
                  onRetry={refetchRules} onEdit={setRuleModal}
                  onDeactivate={(rule) => confirmDeactivate("this work schedule", () => deactivateAvailabilityRule({
                    organizationRef, availabilityRuleId: rule.availabilityRuleId, expectedVersion: rule.version,
                  }))} />
              </Panel>

              <Panel
                hidden={activeSection !== "changes"}
                title="Change hours for a date"
                description="Take a day off or adjust the working hours for one date."
                action={
                  <Button onClick={() => setExceptionModal({})} disabled={!activeRuleId}>
                    Change a date
                  </Button>
                }
              >
                {rulesError || exceptionsPage.isError ? (
                  <Alert severity="error" action={<Button onClick={() => rulesError ? refetchRules() : exceptionsPage.refetch()}>Retry</Button>}>Could not load date changes.</Alert>
                ) : rulesLoading || (!!activeRuleId && exceptionsPage.isLoading) ? (
                  <p role="status">Loading date changes…</p>
                ) : !activeRuleId ? (
                  <EmptyState>Add regular working hours before changing individual dates.</EmptyState>
                ) : exceptions.length === 0 ? (
                  <EmptyState>No date changes. Your regular hours apply.</EmptyState>
                ) : (
                  <Table responsive className="scheduling-table">
                    <thead>
                      <tr>
                        <th>Date</th>
                        <th>Type</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {exceptions.map((item) => (
                        <tr key={item.availabilityExceptionId}>
                          <td>{item.localDate}</td>
                          <td>{item.type === "closed" ? "Day off" : "Adjusted hours"}</td>
                          <td>
                            {item.active ? (
                              <div className="scheduling-row-actions">
                                <Button variant="secondary" onClick={() => setExceptionModal(item)}>
                                  Edit
                                </Button>
                                <Button
                                  variant="secondary"
                                  onClick={() =>
                                    confirmDeactivate("this exception", () =>
                                      deactivateAvailabilityException({
                                        organizationRef,
                                        availabilityExceptionId: item.availabilityExceptionId,
                                        expectedVersion: item.version,
                                      })
                                    )
                                  }
                                >
                                  Restore regular hours
                                </Button>
                              </div>
                            ) : null}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                )}
                <SchedulingPageControls query={exceptionsPage} label="results" />
              </Panel>

              <Panel
                hidden={activeSection !== "changes"}
                title="Extra working hours"
                description="Extra bookable hours that are not part of a weekly rule (e.g. a Saturday clinic)."
                action={<Button onClick={() => setWindowModal({})}>Add extra hours</Button>}
              >
                {windowsPage.isError ? <Alert severity="error" action={<Button onClick={() => windowsPage.refetch()}>Retry</Button>}>Could not load results.</Alert> : windowsPage.isLoading ? <p role="status">Loading…</p> : manualWindows.length === 0 ? (
                  <EmptyState>No extra hours added.</EmptyState>
                ) : (
                  <Table responsive className="scheduling-table">
                    <thead>
                      <tr>
                        <th>Window</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {manualWindows.map((item) => (
                        <tr key={item.availabilityId}>
                          <td>{formatRange(item.startsAt, item.endsAt, schedule?.timezoneName)}</td>
                          <td>
                            <div className="scheduling-row-actions">
                              <Button variant="secondary" onClick={() => setWindowModal(item)}>
                                Edit
                              </Button>
                              <Button
                                variant="secondary"
                                onClick={() =>
                                  confirmDeactivate("this window", () =>
                                    deactivateAvailability({
                                      organizationRef,
                                      availabilityId: item.availabilityId,
                                    })
                                  )
                                }
                              >
                                Deactivate
                              </Button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                )}
                <SchedulingPageControls query={windowsPage} label="results" />
              </Panel>

              <Panel
                hidden={activeSection !== "blocks"}
                title="Blocked time"
                description="Keep time free for meetings, admin or a break. Patients cannot book during these times."
                action={<Button onClick={() => setBlockModal({})}>Block time</Button>}
              >
                {blocksPage.isError ? <Alert severity="error" action={<Button onClick={() => blocksPage.refetch()}>Retry</Button>}>Could not load results.</Alert> : blocksPage.isLoading ? <p role="status">Loading…</p> : blocks.length === 0 ? (
                  <EmptyState>No blocks yet.</EmptyState>
                ) : (
                  <Table responsive className="scheduling-table">
                    <thead>
                      <tr>
                        <th>When</th>
                        <th>Reason</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {blocks.map((item) => (
                        <tr key={item.blockId}>
                          <td>{formatRange(item.startsAt, item.endsAt, schedule?.timezoneName)}</td>
                          <td>{item.reasonCode}</td>
                          <td>
                            <div className="scheduling-row-actions">
                              <Button variant="secondary" onClick={() => setBlockModal(item)}>
                                Edit
                              </Button>
                              <Button
                                variant="secondary"
                                onClick={() =>
                                  confirmDeactivate("this block", () =>
                                    releaseBlock({ organizationRef, blockId: item.blockId })
                                  )
                                }
                              >
                                Release
                              </Button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                )}
                <SchedulingPageControls query={blocksPage} label="results" />
              </Panel>
            </>
          )}
        </div>
      </section>

      {ruleModal !== null ? (
        <AvailabilityRuleForm
          show
          onHide={() => setRuleModal(null)}
          organizationRef={organizationRef}
          schedule={schedule}
          rule={ruleModal.availabilityRuleId ? ruleModal : null}
          existingRules={rules}
        />
      ) : null}
      {exceptionModal !== null ? (
        <ExceptionForm
          show
          onHide={() => setExceptionModal(null)}
          organizationRef={organizationRef}
          schedule={schedule}
          rules={rules}
          exception={exceptionModal.availabilityExceptionId ? exceptionModal : null}
          initialDate={exceptionModal.localDate}
          initialRuleId={exceptionModal.availabilityRuleId}
          occurrenceIssue={exceptionModal.occurrenceIssue}
          onSaved={refetchRules}
        />
      ) : null}
      {windowModal !== null ? (
        <ManualAvailabilityForm
          show
          onHide={() => setWindowModal(null)}
          organizationRef={organizationRef}
          schedule={schedule}
          window={windowModal.availabilityId ? windowModal : null}
        />
      ) : null}
      {blockModal !== null ? (
        <CalendarBlockForm
          show
          onHide={() => setBlockModal(null)}
          organizationRef={organizationRef}
          schedule={schedule}
          block={blockModal.blockId ? blockModal : null}
        />
      ) : null}
    </ScheduleEditorTheme>
  );
};

export default ScheduleManage;
